/**
 * Git 服务 — localStorage 模拟 commit 历史
 *
 * 决策（code-explorer 2026-07-04）：放弃 isomorphic-git 依赖，改用 localStorage
 * 理由：
 *   1. demo 阶段用户最终目的是"看到 commit 历史列表"，不真在浏览器内建 git 仓库
 *   2. isomorphic-git + lightning-fs 在浏览器内增加 ~200KB，与 demo 简化原则冲突
 *   3. localStorage 序列化/反序列化最快实现，pnpm verify 0 依赖
 *
 * 数据结构：
 *   history: { commits: [{ hash, timestamp, code, message, author }] }
 *
 * 屏 4 auto-commit：调用 pushCommit(projectId, code) → 返回新 commit 对象
 * 屏 5.5 录像回放：可调用 listCommits(projectId) 显示提交列表（v1+）
 */

import { getProject, saveProject } from "../storage/project-store";
import { dataProxy } from "../core/data-proxy.js";

export interface Commit {
  hash: string;          // 8 字符短 hash（伪 SHA-1）
  timestamp: number;     // Date.now()
  code: string;          // 完整 bot 代码（屏 4 auto-commit 时整文件存）
  message: string;       // commit message
  author: string;        // demo 锁 "OPERATOR"
  projectId: string;     // 关联到 Project.id
}

interface HistoryStore {
  commits: Commit[];
}

/** 简单伪 hash（FNV-1a 32-bit，取 hex 8 位）—— demo 用，不保证密码学安全 */
function pseudoHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  // 加一个时间戳尾巴避免碰撞
  const tail = Date.now().toString(36).slice(-4);
  return h.toString(16).padStart(8, "0") + tail;
}

function readHistory(): HistoryStore {
  return dataProxy.get<HistoryStore>("git-history") ?? { commits: [] };
}

function writeHistory(store: HistoryStore): void {
  dataProxy.set("git-history", store);
}

/** 列出某项目的所有 commit（最新在前） */
export function listCommits(projectId: string): Commit[] {
  const all = readHistory().commits;
  return all
    .filter((c) => c.projectId === projectId)
    .sort((a, b) => b.timestamp - a.timestamp);
}

/** 推一条新 commit（屏 4 战斗前 auto-commit 调） */
export function pushCommit(
  projectId: string,
  code: string,
  message = "auto-commit: pre-battle snapshot",
  author = "OPERATOR",
): Commit {
  const project = getProject(projectId);
  if (!project) {
    throw new Error(`pushCommit: project ${projectId} not found`);
  }

  const commit: Commit = {
    hash: pseudoHash(`${projectId}-${code}-${Date.now()}`),
    timestamp: Date.now(),
    code,
    message,
    author,
    projectId,
  };

  const store = readHistory();
  store.commits.push(commit);
  writeHistory(store);

  // 同步到 Project.commitCount（保持原逻辑兼容）
  saveProject({ ...project, code, commitCount: project.commitCount + 1, updatedAt: Date.now() });

  return commit;
}

/** 取某 commit 的完整代码（屏 5.5 录像/历史回看用，v1+ 扩展） */
export function getCommit(hash: string): Commit | null {
  return readHistory().commits.find((c) => c.hash === hash) ?? null;
}

/** 统计：项目总 commit 数（屏 2 顶栏可显示 "commit #N"） */
export function getCommitCount(projectId: string): number {
  return listCommits(projectId).length;
}

/** 调试用：清空某项目的所有 commit（不要在屏 2/4 UI 暴露） */
export function _debugClearHistory(projectId?: string): void {
  if (!projectId) {
    dataProxy.remove("git-history");
    return;
  }
  const store = readHistory();
  store.commits = store.commits.filter((c) => c.projectId !== projectId);
  writeHistory(store);
}

/** P2-#4:删除某项目的所有 commit(级联清理——屏 1 删除项目时调)
 *  与 _debugClearHistory 区别:这是公开 API,仅支持单 projectId,无全局清空 */
export function deleteProjectCommits(projectId: string): void {
  const store = readHistory();
  const before = store.commits.length;
  store.commits = store.commits.filter((c) => c.projectId !== projectId);
  const removed = before - store.commits.length;
  if (removed > 0) writeHistory(store);
}
